import type { EntityLinkKind } from "~/utils/types/assistant-report-artifact";

import { ENTITY_RECORD_DESCRIPTORS, entityKindForHref, entityKindForTag } from "./entity-link";

/**
 * Entity tag pattern: [Type:Name:ID] or [Type:ID]
 * Requires at least one colon to distinguish from Markdown link labels.
 * Negative lookahead (?!\() prevents matching [label]( in Markdown links.
 * A match is a tag only when isEntityTag agrees; `[Acme: West]` or `[10:30]` stay prose.
 */
const entityLinkTagRegex = /\[([^\]:]+):((?:[^\]:]+:?)+)\](?!\()/g;

const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
const exactUuidPattern = new RegExp(`^${uuidPattern.source}$`, "i");

type ParsedEntityTag = {
  entityType: string;
  name: string | null;
  identifier: string | null;
};

const resolveEntityHref = (entityType: string, identifier: string | null, number?: string | null): string | null => {
  const kind = entityKindForTag(entityType);
  if (!identifier || !kind) {
    return null;
  }
  const descriptor = ENTITY_RECORD_DESCRIPTORS[kind];
  return (
    descriptor.proseHref?.(identifier) ??
    descriptor.href(identifier, number ?? identifier, descriptor.routeKind === "payment_batch" ? identifier : undefined)
  );
};

/**
 * Parse entity tag using capture groups from the regex match.
 * This avoids re-executing the global regex during String.prototype.replace iteration.
 */
const parseEntityTagFromCaptures = (rawType: string, rawRest: string): ParsedEntityTag => {
  const parts = rawRest.split(":").filter(Boolean);
  const name = parts.length === 2 ? parts[0] : null;
  const identifier = parts.length === 2 ? parts[1] : (parts[0] ?? null);

  return {
    entityType: rawType.trim().toLowerCase(),
    name: name?.trim() ?? null,
    identifier: identifier?.trim() ?? null,
  };
};

type EntityTagTransform = {
  /**
   * Convert a parsed entity tag into a string for downstream use.
   * Returning null omits the tag entirely.
   */
  toDisplay: (tag: ParsedEntityTag) => string | null;
};

type TransformMessageArgs = {
  transform: EntityTagTransform;
};

const defaultTransform: EntityTagTransform = {
  toDisplay: tag => tag.name ?? null,
};

/** A bracketed colon pair is an entity tag only for a type we can open or a UUID-shaped identifier. */
const isEntityTag = (tag: ParsedEntityTag): boolean =>
  entityKindForTag(tag.entityType) !== null || (tag.identifier !== null && exactUuidPattern.test(tag.identifier));

export const transformAiMessage = (message: string, args: TransformMessageArgs = { transform: defaultTransform }): string => {
  const { transform } = args;
  return message.replace(entityLinkTagRegex, (match: string, rawType: string, rawRest: string) => {
    const parsed = parseEntityTagFromCaptures(rawType, rawRest);
    if (!isEntityTag(parsed)) {
      return match;
    }
    const transformed = transform.toDisplay(parsed);
    return transformed ?? "";
  });
};

// Inline and fenced code are masked before tag resolution and bracket unwrapping, then restored untouched.
const CODE_SPAN_PATTERN = /```[\s\S]*?```|`[^`\n]*`/g;
const maskCode = (message: string): { masked: string; restore: (value: string) => string } => {
  const spans: string[] = [];
  const masked = message.replace(CODE_SPAN_PATTERN, span => {
    spans.push(span);
    return `\u0000${spans.length - 1}\u0000`;
  });
  return { masked, restore: value => value.replace(/\u0000(\d+)\u0000/g, (_token, index: string) => spans[Number(index)] ?? "") };
};

const withCodeMasked = (message: string, transform: (masked: string) => string): string => {
  const { masked, restore } = maskCode(message);
  return restore(transform(masked));
};

const BARE_BRACKET_PATTERN = /\[([^[\]\n]+)\]/g;
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * A bare `[Name]` the model wrote around a name renders as the name. Markdown syntax is left alone: links,
 * images, references and their definitions, footnotes, task boxes, and short tokens such as `[1]` or `[10:30]`.
 * Runs after entity-tag resolution, on closed single-line pairs only, so a streaming `[Acme` never flickers.
 */
export const unwrapBareBrackets = (message: string): string =>
  message.replace(BARE_BRACKET_PATTERN, (match: string, label: string, offset: number, whole: string) => {
    const before = offset > 0 ? (whole[offset - 1] ?? "") : "";
    const after = whole[offset + match.length] ?? "";
    const alphanumerics = label.match(/[\p{L}\p{N}]/gu)?.length ?? 0;
    if (
      alphanumerics < 2 ||
      !/\p{L}/u.test(label) ||
      label.startsWith("^") ||
      ["!", "]", "[", "\\"].includes(before) ||
      ["(", "[", "]", ":"].includes(after) ||
      new RegExp(`^\\s{0,3}\\[${escapeRegExp(label)}\\]:`, "im").test(whole)
    ) {
      return match;
    }
    return label;
  });

type PlainTextOptions = {
  preserveLineBreaks?: boolean;
};

type ReplaceWith = string | ((match: string, ...args: unknown[]) => string);
const markdownStructuralPatterns: { regex: RegExp; replaceWith: ReplaceWith }[] = [
  { regex: /^\s{0,3}\*\s+/gm, replaceWith: "• " },
  { regex: /^\s{0,3}-\s+/gm, replaceWith: "• " },
  { regex: /^\s{0,3}\d+\.\s+/gm, replaceWith: (match: string) => match.replace(/\s+/g, " ") },
  { regex: /^>\s?/gm, replaceWith: "" },
  { regex: /`([^`]+)`/g, replaceWith: "$1" },
  { regex: /```[\s\S]*?```/gm, replaceWith: (match: string) => match.replace(/```/g, "") },
  { regex: /\*\*([^*]+)\*\*/g, replaceWith: "$1" },
  { regex: /__([^_]+)__/g, replaceWith: "$1" },
  { regex: /\*([^*]+)\*/g, replaceWith: "$1" },
  { regex: /_([^_]+)_/g, replaceWith: "$1" },
  { regex: /~~([^~]+)~~/g, replaceWith: "$1" },
  { regex: /^#+\s*/gm, replaceWith: "" },
  { regex: /\[(.*?)\]\((.*?)\)/g, replaceWith: "$1" },
];

const applyMarkdownStripping = (input: string): string => {
  let output = input;
  for (const pattern of markdownStructuralPatterns) {
    if (typeof pattern.replaceWith === "string") {
      output = output.replace(pattern.regex, pattern.replaceWith);
    } else {
      output = output.replace(pattern.regex, pattern.replaceWith);
    }
  }

  return output;
};

const normalizeSpacing = (input: string, opts: PlainTextOptions): string => {
  let output = input;

  if (opts.preserveLineBreaks !== false) {
    output = output.replace(/\r?\n{3,}/g, "\n\n");
  } else {
    output = output.replace(/\r?\n/g, " ");
  }

  return output
    .replace(/[\t\f\v]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
};

// Decode only receipt name encodings, once, after link/tag processing so literal brackets cannot become links.
// A single pass preserves literal entity spellings; numeric spellings also carry the receipt's escaped #.
const decodeReceiptNameEntities = (value: string): string =>
  value.replace(
    /&amp;(?:((?:amp;)*)\\#([0-9]+|[xX][0-9a-fA-F]+);)?|&#(?:91|93);/g,
    (entity, ampLayer: string | undefined, numeric: string | undefined) => {
      if (numeric !== undefined) {
        return `&${ampLayer ?? ""}#${numeric};`;
      }
      return entity === "&#91;" ? "[" : entity === "&#93;" ? "]" : "&";
    },
  );

export const formatAiMessageToPlainText = (message: string, opts: PlainTextOptions = {}): string => {
  const literals = maskClipboardLiterals(message, "text");
  const withoutEntityTags = withCodeMasked(literals.masked, masked =>
    unwrapBareBrackets(
      transformAiMessage(masked, {
        transform: {
          toDisplay: tag => tag.name ?? tag.identifier ?? "",
        },
      }),
    ),
  );

  const strippedMarkdown = applyMarkdownStripping(withoutEntityTags);
  return normalizeSpacing(literals.restore(decodeReceiptNameEntities(strippedMarkdown)), opts);
};

export const resolveEntityLink = resolveEntityHref;

const firstNonEmpty = (...values: (string | null | undefined)[]): string => {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return "";
};

const stripMalformedEntityMarkdownLinks = (message: string): string => {
  return message.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label: string, path: string) => (entityKindForHref(path) ? match : label));
};

const stripIncompleteEntityTagSuffix = (message: string): string =>
  message.replace(/\[([^\]:\n]+):([^\]\n]*)$/, (match: string, rawType: string, rawRest: string) => {
    const [rawName, rawIdentifier] = rawRest.split(":");
    const entityType = rawType.trim();
    const name = rawName?.trim() ?? "";
    // The same test as a closed tag: an opening that is not a known type or an identifier is ordinary prose.
    const identifierLike = [rawIdentifier, rawName].some(part => /^[a-f0-9-]{8,}$/i.test(part?.trim() ?? ""));
    if (entityKindForTag(entityType.toLowerCase()) === null && !identifierLike) {
      return match;
    }
    if (rawIdentifier !== undefined) {
      return firstNonEmpty(name, entityType);
    }
    return exactUuidPattern.test(name) || /^[a-f0-9-]{8,}$/i.test(name) ? entityType : firstNonEmpty(name, entityType);
  });

const customerMarkdownLinkPattern = /\[([^\]]+)\]\(\/customers\/([a-f0-9-]{36})(?:\/[^)]*)?\)/gi;
const customerPathIdPattern = /\/customers\/([a-f0-9-]{36})/gi;

export const extractCustomerIdsFromAiMessage = (message: string): string[] => {
  const customerIds = new Set<string>();
  for (const match of message.matchAll(entityLinkTagRegex)) {
    const rawType = match[1];
    const rawRest = match[2];
    if (!rawType || !rawRest) {
      continue;
    }
    const parsed = parseEntityTagFromCaptures(rawType, rawRest);
    if (parsed.entityType === "customer" && parsed.identifier && exactUuidPattern.test(parsed.identifier)) {
      customerIds.add(parsed.identifier);
    }
  }
  for (const match of message.matchAll(customerMarkdownLinkPattern)) {
    const customerId = match[2];
    if (customerId) {
      customerIds.add(customerId);
    }
  }
  for (const match of message.matchAll(customerPathIdPattern)) {
    const customerId = match[1];
    if (customerId) {
      customerIds.add(customerId);
    }
  }
  return [...customerIds];
};

const stripOutOfScopeCustomerMarkdownLinks = (message: string, customerIdsInViewScope: ReadonlySet<string> | null | undefined): string => {
  if (customerIdsInViewScope == null) {
    return message;
  }
  return message.replace(customerMarkdownLinkPattern, (match, label: string, customerId: string) =>
    customerIdsInViewScope.has(customerId) ? match : boldLabel(label),
  );
};

type FormatAiMessageForDisplayOptions = {
  /** When set, customer entity tags and /customers/{id} markdown links render as the bold name if id is absent. */
  customerIdsInViewScope?: ReadonlySet<string> | null;
  /** Module permission for each record kind (entityLinkAllowed); a denied kind renders the bold name. */
  entityLinkAllowed?: (kind: EntityLinkKind) => boolean;
};

const boldLabel = (label: string): string => `**${label}**`;

/**
 * Convert authorized entity tags to record links for both Assistant renderers.
 * Unsupported or incomplete tags remain readable text and malformed direct links
 * are stripped so model-authored paths cannot bypass the entity-reference contract.
 */
export const formatAiMessageForDisplay = (message: string, options?: FormatAiMessageForDisplayOptions): string => {
  const withEntityLinks = withCodeMasked(message, masked =>
    unwrapBareBrackets(
      transformAiMessage(masked, {
        transform: {
          toDisplay: tag => {
            const identifierLabel = tag.identifier && !exactUuidPattern.test(tag.identifier) ? tag.identifier : null;
            const label = firstNonEmpty(tag.name, identifierLabel, tag.entityType);
            const href = resolveEntityHref(tag.entityType, tag.identifier, tag.name);
            const kind = entityKindForTag(tag.entityType);
            if (href && kind && options?.entityLinkAllowed && !options.entityLinkAllowed(kind)) {
              return boldLabel(label);
            }
            if (
              href &&
              tag.entityType === "customer" &&
              tag.identifier &&
              options?.customerIdsInViewScope != null &&
              !options.customerIdsInViewScope.has(tag.identifier)
            ) {
              return boldLabel(label);
            }
            return href ? ` [${label}](${href}) ` : label;
          },
        },
      }),
    ),
  );

  return stripIncompleteEntityTagSuffix(
    stripOutOfScopeCustomerMarkdownLinks(stripMalformedEntityMarkdownLinks(withEntityLinks), options?.customerIdsInViewScope),
  );
};

/**
 * Copy-only safety: scrub internal identifiers from content before placing it on the clipboard.
 * This removes parenthetical IDs and bare UUIDs while keeping natural language intact.
 */
const internalIdPatterns: RegExp[] = [
  /\(Org ID:\s*[a-f0-9-]+\)/gi, // (Org ID: c1d24457-...)
  /\(ID:\s*[a-f0-9-]+\)/gi, // (ID: abc123...)
  /\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/gi, // standalone UUIDs
];

export const scrubInternalIds = (input: string): string => {
  let output = input;
  for (const pattern of internalIdPatterns) {
    output = output.replace(pattern, "");
  }
  return output.replace(/\s{2,}/g, " ").trim();
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// Keep escaped punctuation and code opaque until clipboard parsing finishes. Restored HTML literals are
// escaped separately; neither entity decoding nor Markdown can reinterpret their contents as links/markup.
const maskClipboardLiterals = (message: string, format: "html" | "text"): { masked: string; restore: (value: string) => string } => {
  const literals: string[] = [];
  const pattern = /\\([\\`*_#<>~|[\]])|```[\s\S]*?```|`[^`\n]*`/g;
  const masked = message.replace(pattern, (match, punctuation: string | undefined) => {
    let literal: string;
    if (punctuation !== undefined) {
      literal = format === "html" ? escapeHtml(punctuation) : punctuation;
    } else {
      const fenced = match.startsWith("```");
      const code = fenced ? match.slice(3, -3) : match.slice(1, -1);
      if (format === "text") {
        literal = code;
      } else if (fenced) {
        literal = `<pre><code>${escapeHtml(code.trim())}</code></pre>`;
      } else {
        literal = `<code>${escapeHtml(code)}</code>`;
      }
    }
    literals.push(literal);
    return `\u0000clipboard:${literals.length - 1}\u0000`;
  });
  return {
    masked,
    restore: value => value.replace(/\u0000clipboard:(\d+)\u0000/g, (_token, index: string) => literals[Number(index)] ?? ""),
  };
};

/**
 * Minimal Markdown→HTML for clipboard use only (no links). Covers strong/em, code, lists, and paragraphs.
 * We intentionally omit anchors and convert headings to bold paragraphs for email client fidelity.
 * Content is escaped upfront to prevent HTML injection; Markdown replacements wrap already-escaped text.
 */
export const formatAiMessageToHtml = (message: string): string => {
  const literals = maskClipboardLiterals(message, "html");
  // Replace entity tags with display names only
  let content = withCodeMasked(literals.masked, masked =>
    unwrapBareBrackets(
      transformAiMessage(masked, {
        transform: { toDisplay: tag => tag.name ?? tag.identifier ?? "" },
      }),
    ),
  );

  // Remove Markdown links entirely, keep the label
  content = content.replace(/\[(.*?)\]\((.*?)\)/g, "$1");

  // Escape all content upfront to prevent HTML injection
  content = escapeHtml(decodeReceiptNameEntities(content));

  // Strong/Emphasis/Strike (order matters; content already escaped)
  content = content.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  content = content.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  content = content.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  content = content.replace(/_([^_]+)_/g, "<em>$1</em>");
  content = content.replace(/~~([^~]+)~~/g, "$1");

  const lines = content.split(/\r?\n/);
  const htmlParts: string[] = [];
  let listMode: null | "ul" | "ol" = null;

  const closeListIfOpen = () => {
    if (listMode) {
      htmlParts.push(listMode === "ul" ? "</ul>" : "</ol>");
      listMode = null;
    }
  };

  const openListIfNeeded = (mode: "ul" | "ol") => {
    if (listMode !== mode) {
      closeListIfOpen();
      listMode = mode;
      htmlParts.push(mode === "ul" ? "<ul>" : "<ol>");
    }
  };

  let paragraphBuffer: string[] = [];
  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      const paragraphHtml = paragraphBuffer.join("<br/>");
      htmlParts.push(`<p>${paragraphHtml}</p>`);
      paragraphBuffer = [];
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/g, "");
    if (!line.trim()) {
      closeListIfOpen();
      flushParagraph();
      continue;
    }

    const ulMatch = /^\s{0,3}[-*]\s+(.+)/.exec(line);
    if (ulMatch) {
      flushParagraph();
      openListIfNeeded("ul");
      htmlParts.push(`<li>${ulMatch[1]}</li>`);
      continue;
    }

    const olMatch = /^\s{0,3}(\d+)\.\s+(.+)/.exec(line);
    if (olMatch) {
      flushParagraph();
      openListIfNeeded("ol");
      htmlParts.push(`<li>${olMatch[2]}</li>`);
      continue;
    }

    closeListIfOpen();
    const heading = /^\s{0,3}#{1,6}\s+(.+)/.exec(line);
    if (heading) {
      flushParagraph();
      htmlParts.push(`<p><strong>${heading[1]}</strong></p>`);
      continue;
    }

    paragraphBuffer.push(line);
  }

  closeListIfOpen();
  flushParagraph();

  // Wrap in a neutral container for email clients
  return literals.restore(`<div>${htmlParts.join("")}</div>`);
};

/**
 * Build sanitized clipboard content (HTML + plain text) and scrub identifiers.
 * UI rendering is unaffected; scrubbing is applied only for clipboard copy.
 */
export const formatAiMessageForClipboard = (
  message: string,
  opts: { preserveLineBreaks?: boolean } = {},
): { html: string; text: string } => {
  const html = scrubInternalIds(formatAiMessageToHtml(message));
  const text = scrubInternalIds(formatAiMessageToPlainText(message, { preserveLineBreaks: opts.preserveLineBreaks }));
  return { html, text };
};
